import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";

/**
 * s228 (INTERVIEW-SAVE-FAILURE, D-227.31) — WHAT THE PLANNER IS TOLD WHEN THE SERVER REFUSES AN INTERVIEW SAVE.
 *
 * Before this, no interview save handled an error: a refused save showed nothing, and the old value came back on the
 * next reload. Reade's two rulings (s228):
 *   1. "A red pop-up in the corner that says "Couldn't save that," with the server's reason underneath."
 *   2. "What the planner typed stays in the box, so they can fix it or try again."
 *
 * The pop-up is the product's existing one (ProtectYourAccess.tsx, ExecutorNeedsPanel.tsx, CaptureUpstreamRenderer.tsx
 * all say "Couldn't save that"). The board declares no failed-save state (s227: 0 hits across 35 boards), so this is
 * product precedent, not a board port.
 *
 * HOW IT IS WIRED — one place per save hook, enforced by `interview-save-failure-scan.test.ts`:
 *   - every interview `useMutation` that writes passes `onError: onSaveFailed` (the pop-up goes up ONCE, from the
 *     mutation, whoever called it);
 *   - the writers keep THROWING, so a sequence (a flag, then its rows) stops at the first refusal instead of writing
 *     half of what the planner asked for;
 *   - a save started from an event handler goes through `saveInBackground`, which stops an already-reported refusal
 *     escaping as an unhandled rejection. Anything it was NOT told about still escapes, so a real bug stays loud.
 *
 * Ruling 2 needs nothing here for the in-row boxes: a box holds its own draft and only resets when the SAVED value
 * changes, which a refused save never does. The two panels that close on Done (the contact card and the writing
 * space) wait for the save and stay open when it is refused.
 */

export const SAVE_FAILED_TITLE = "Couldn't save that";
/** A 5xx: the server's own message is "Internal Server Error", which tells a planner nothing. */
export const SAVE_FAILED_SERVER = "Something went wrong on our side. Please try again.";
/** No response at all. The product's existing sentence (ExecutorNeedsPanel `loadStatusMessage`). */
export const SAVE_FAILED_OFFLINE = "We couldn't reach the server. Check your connection and try again.";

const NETWORK_FAILURE = /failed to fetch|networkerror|load failed|network request failed/i;

/**
 * The line under the title. `null` = show nothing: a 401 is already sending the planner to sign in
 * (queryClient.ts `redirectToLogin`). Pure.
 *
 * `apiRequest` throws `"<status>: <body>"`; the body is the E-1 envelope `{ message }` (async-handler.ts
 * `errorEnvelope`), or `{ error }` from a route that answers directly, or plain text.
 */
export function saveFailureReason(err: unknown): string | null {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  const m = /^(\d{3}): ?([\s\S]*)$/.exec(msg);
  if (!m) return err instanceof TypeError && NETWORK_FAILURE.test(msg) ? SAVE_FAILED_OFFLINE : SAVE_FAILED_SERVER;
  const status = Number(m[1]);
  if (status === 401) return null;
  if (status >= 500) return SAVE_FAILED_SERVER;
  const body = m[2].trim();
  let reason = body;
  try {
    const j = JSON.parse(body);
    reason = typeof j?.message === "string" ? j.message : typeof j?.error === "string" ? j.error : "";
  } catch {
    // plain text — used as it is, unless it is a page of HTML
    if (body.startsWith("<")) reason = "";
  }
  return reason.trim() || SAVE_FAILED_SERVER;
}

const reported = new WeakSet<object>();

/** The mutation's `onError` for every interview save: the red pop-up, once. */
export function useSaveFailure(): (err: unknown) => void {
  const { toast } = useToast();
  return useCallback(
    (err: unknown) => {
      if (err && typeof err === "object") reported.add(err);
      const reason = saveFailureReason(err);
      if (reason === null) return;
      toast({ title: SAVE_FAILED_TITLE, description: reason, variant: "destructive" });
    },
    [toast],
  );
}

/**
 * Start a save from an event handler without awaiting it. A refusal the pop-up already reported is dropped here;
 * anything else is re-thrown, so an unexpected failure is not silenced.
 */
export function saveInBackground(save: unknown): void {
  void Promise.resolve(save).catch((err: unknown) => {
    if (err && typeof err === "object" && reported.has(err)) return;
    throw err;
  });
}

/**
 * Wait for a save a panel's Done started: `true` when it landed (or there was nothing to wait for), `false` when the
 * server refused it and the pop-up said so — the panel then stays open with what was typed (ruling 2). Anything
 * else is re-thrown.
 */
export async function saveSettled(save: unknown): Promise<boolean> {
  try {
    await save;
    return true;
  } catch (err) {
    if (err && typeof err === "object" && reported.has(err)) return false;
    throw err;
  }
}

/**
 * A panel's Done: run `then` (close the panel) once the save has landed — at once when there is nothing to wait
 * for, never when the server refused it. Unexpected failures still escape.
 */
export function whenSaved(save: unknown, then: () => void): void {
  if (!save || typeof (save as PromiseLike<unknown>).then !== "function") {
    then();
    return;
  }
  void saveSettled(save).then((ok) => {
    if (ok) then();
  });
}
